import React, { FC, useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';

import { ReactComponent as FlowerGirl } from 'src/public/ds/illustrations/flower-girl.svg';
import { SessionSlot } from 'src/modules/videoSessions/models/videoSessions';
import { CoachNode } from 'src/generated/webapp_gql';
import { RouteComponentProps } from 'react-router-dom';
import { analyticsClient } from 'src/shared/services/analytics';
import { addToCalendar } from 'src/modules/videoSessions/helpers/addToCalendar';
import { FormattedSessionDateTimeRange } from 'src/modules/videoSessions/DateTimeFormatter';

import {
    ButtonGroup,
    Button,
    HeadingText50,
    BodyTextMedium,
    BodyTextLarge,
    pxToRem,
} from '@modernhealth/vital-components';

const PageContainer = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: pxToRem(524),
    marginTop: '3rem',
    marginLeft: 'auto',
    marginRight: 'auto',
    paddingBottom: '3rem',
    padding: '0 1rem',
});

const StyledFlowerGirl = styled(FlowerGirl)({
    marginBottom: '2rem',
});

const TextContainer = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'start',
    width: '100%',
});

const StyledButton = styled(Button)({
    marginTop: pxToRem(15),
});

interface LocationState {
    sessionSlot: SessionSlot;
    sessionBookedUuid: string;
    provider: CoachNode;
}

export const ExtendTherapyLastSessionPage: FC<RouteComponentProps<{}, {}, LocationState>> = ({
    history,
    location,
}) => {
    const { t } = useTranslation();
    const state = location?.state;
    const [eventAdded, setEventAdded] = useState(false);

    if (!state || !Object.keys(state).length) {
        history.push('/extend-therapy-sessions');
        return null;
    }

    const { sessionSlot, sessionBookedUuid, provider } = state;

    const handleAddToCalendar = () => {
        setEventAdded(true);
        addToCalendar(sessionBookedUuid);
    };

    const handleCheckCoverage = () => {
        analyticsClient.trackClick('Extend Therapy Sessions: Extend', {
            page_name: 'Final session booked',
        });
        history.push('/inquire-employer-insurance-coverage');
    };

    return (
        <PageContainer>
            <StyledFlowerGirl />
            <TextContainer>
                <HeadingText50 marginBottom={8}>
                    {t('insuranceForm:extended_therapy_access_last_session.title', {
                        providerFirstName: provider?.firstName,
                    })}
                </HeadingText50>
                <BodyTextLarge marginBottom={8}>
                    <FormattedSessionDateTimeRange
                        start={sessionSlot.start.toDate()}
                        end={sessionSlot.end.toDate()}
                    />
                </BodyTextLarge>
                <BodyTextMedium marginBottom={24}>
                    {t('insuranceForm:extended_therapy_access_last_session.description')}
                </BodyTextMedium>
            </TextContainer>
            <ButtonGroup>
                <Button variant="secondary" size="large" onClick={handleAddToCalendar}>
                    {t(
                        eventAdded
                            ? 'insuranceForm:extended_therapy_access_last_session.added_to_calendar'
                            : 'insuranceForm:extended_therapy_access_last_session.add_to_calendar',
                    )}
                </Button>
                <Button
                    data-testid="ButtonGroupTestID.SecondButton"
                    variant="primary"
                    size="large"
                    onClick={handleCheckCoverage}
                >
                    {t(
                        'insuranceForm:extended_therapy_access_last_session.extended_therapy_access',
                    )}
                </Button>
            </ButtonGroup>
            <StyledButton onClick={() => history.push('/dashboard')} variant="flat" size="large">
                {t('insuranceForm:extended_therapy_access_last_session.back_to_home')}
            </StyledButton>
        </PageContainer>
    );
};
